import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BackBar, Page } from "../components/Band";
import { Button, Content, Empty, PageSpinner } from "../components/primitives";
import "../styles/manage-web.css";
import { useAdminClubs } from "./manage/useAdminClubs";
import { ERR_TEXT, TAB_KEYS, manageHref } from "./manage/manageUtils";
import ClubManager from "./manage/ClubManager";

/** Manage Club: /manage and /manage/:clubId/:tab? (mockup viewAdmin; mirrors screens/AdminScreen.js). */
export default function ManagePage() {
  const { clubId: paramClubId, "*": splat } = useParams();
  const navigate = useNavigate();
  const { adminClubs, loading, remoteLoaded, error, refresh, patchClub, dropLeadership } = useAdminClubs();

  const seg = (splat || "").split("/")[0];
  const tab = TAB_KEYS.includes(seg) ? seg : "overview";
  // A club named in the URL is authoritative (utils/manageClubRouting.js resolveManageClub): an
  // unknown / lost club is never swapped for another one. Only bare /manage opens the first club.
  const club = useMemo(
    () => (paramClubId ? adminClubs.find((c) => c.id === paramClubId) || null : adminClubs[0] || null),
    [adminClubs, paramClubId]
  );
  // A deep link to a club missing from a possibly stale context list waits for the fresh list.
  const awaitingTarget = !!paramClubId && !club && !remoteLoaded && !error;

  useEffect(() => {
    if (club && !paramClubId) navigate(manageHref(club.id, tab), { replace: true });
  }, [club, paramClubId, tab, navigate]);

  const onTransferred = useCallback(async (clubId) => {
    const remaining = await dropLeadership(clubId);
    if (remaining.length === 0) navigate("/clubs", { replace: true });
    else navigate(manageHref(remaining[0].id), { replace: true });
  }, [dropLeadership, navigate]);

  if (loading || awaitingTarget) {
    return <Page><BackBar title="Manage Club" testId="manage" /><PageSpinner /></Page>;
  }

  if (error && !club) {
    return (
      <Page>
        <BackBar title="Manage Club" testId="manage" />
        <Content>
          <Empty icon="alert" title="Couldn't load your clubs">
            <div>{ERR_TEXT}</div>
            <Button size="sm" style={{ marginTop: 10 }} onClick={refresh} data-testid="manage.retry">Retry</Button>
          </Empty>
        </Content>
      </Page>
    );
  }

  if (!club && paramClubId && adminClubs.length > 0) {
    return (
      <Page>
        <BackBar title="Manage Club" testId="manage" />
        <Content>
          <Empty icon="shield">
            <div>You no longer manage this club.</div>
            <Link className="link" to="/manage" data-testid="manage.yourClubs" style={{ display: "inline-block", marginTop: 10 }}>Manage your clubs</Link>
          </Empty>
        </Content>
      </Page>
    );
  }

  if (!club) {
    return (
      <Page>
        <BackBar title="Manage Club" testId="manage" />
        <Content>
          <Empty icon="shield">
            <div>Only club leaders can open this page.</div>
            <Link className="link" to="/clubs" data-testid="manage.browseClubs" style={{ display: "inline-block", marginTop: 10 }}>Browse clubs</Link>
          </Empty>
        </Content>
      </Page>
    );
  }

  return (
    <ClubManager
      key={club.id}
      club={club}
      clubs={adminClubs}
      tab={tab}
      onRefresh={refresh}
      onPatchClub={patchClub}
      onTransferred={onTransferred}
    />
  );
}
